import {
  BriefcaseBusiness,
  Check,
  ChevronRight,
  CirclePause,
  FileCheck2,
  FileText,
  House,
  ListChecks,
  MousePointer2,
  Settings,
  ShieldCheck,
  UserRound
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useActiveMotion } from "../hooks/useActiveMotion";
import { useTargetCursor } from "../hooks/useTargetCursor";
import { AgentPulseIcon } from "./AgentPulseIcon";
import { OffrLogo } from "./OffrLogo";

const jobs = [
  { name: "Northstar", role: "Frontend Engineer", mark: "N", tone: "mint" },
  { name: "Paperplane", role: "Product Engineer", mark: "P", tone: "violet" },
  { name: "Kernel", role: "Software Engineer", mark: "K", tone: "ink" },
  { name: "Daybreak", role: "UI Engineer", mark: "D", tone: "coral" }
] as const;

const stages = [
  { label: "You add the facts once", target: null, instruction: 0 },
  { label: "Four chosen tabs are ready", target: null, instruction: 1 },
  { label: "Open Offr from the browser", target: "extension-icon", instruction: 2 },
  { label: "Start one attended queue", target: "apply-all", instruction: 2 },
  { label: "Mapping verified profile facts", target: null, instruction: 3 },
  { label: "Drafting one role answer", target: null, instruction: 3 },
  { label: "Tailoring the ATS-aware resume", target: null, instruction: 3 },
  { label: "Waiting for your approval", target: "confirm-button", instruction: 4 },
  { label: "Submitted; the next tab stays queued", target: null, instruction: 4 }
] as const;

const instructions = [
  {
    short: "Profile",
    title: "Save your profile once.",
    copy: "Add verified facts, a base resume, and writing samples. Once."
  },
  {
    short: "Jobs",
    title: "Choose the jobs you want.",
    copy: "Open the external application tabs you actually want."
  },
  {
    short: "Start",
    title: "Start Offr. Click Apply All.",
    copy: "Open the extension and start one visible, sequential queue."
  },
  {
    short: "Prepare",
    title: "Agent prepares each one.",
    copy: "Offr fills the form and tailors one factual resume for each role."
  },
  {
    short: "Approve",
    title: "You approve and submit.",
    copy: "Review uncertain answers and confirm each submission."
  }
] as const;

const instructionStages = [[0], [1], [2, 3], [4, 5, 6], [7, 8]] as const;

function jobState(index: number, stage: number) {
  if (index === 0) {
    if (stage < 3) return "Ready";
    if (stage < 7) return "Preparing";
    if (stage === 7) return "Review";
    return "Submitted";
  }
  if (index === 1 && stage === 8) return "Next";
  return "Queued";
}

export function AgentDemo() {
  const [stage, setStage] = useState(0);
  const { ref: motionRef, motionActive, prefersReducedMotion } =
    useActiveMotion<HTMLDivElement>(0.08);
  const productRef = useRef<HTMLDivElement>(null);
  const productFrameRef = useRef<HTMLDivElement>(null);
  const chapterRefs = useRef<Array<HTMLLIElement | null>>([]);
  const mobileTimersRef = useRef<number[]>([]);
  const mobileInstructionRef = useRef(0);
  const mobileAutoplayRef = useRef<number | null>(null);
  const activeInstructionRef = useRef(0);
  const cursor = useTargetCursor(productRef, stages[stage].target);
  const activeInstructionIndex = stages[stage].instruction;
  const cursorVisible = stages[stage].target !== null && [2, 3, 7].includes(stage);
  const extensionOpen = stage >= 2;
  const applying = stage >= 4 && stage <= 6;
  const reviewOpen = stage >= 7;
  const submitted = stage === 8;
  const applyProgress = stage < 4 ? 0 : stage === 4 ? 32 : stage === 5 ? 66 : stage === 6 ? 92 : 100;
  const applyLabel = applying
    ? "Applying"
    : reviewOpen
      ? submitted
        ? "Next tab ready"
        : "Review ready"
      : "Apply all (4)";
  const agentAction = stage === 4
    ? "Filling verified facts"
    : stage === 5
      ? "Drafting in your voice"
      : stage === 6
        ? "Tailoring role resume"
        : stage >= 7
          ? "Preparation complete"
          : "Ready to prepare";

  const clearMobileTimers = useCallback(() => {
    mobileTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    mobileTimersRef.current = [];
  }, []);

  const playInstruction = useCallback((index: number, beat = 900) => {
    const mappedStages = instructionStages[index];
    if (!mappedStages) return;

    clearMobileTimers();
    mobileInstructionRef.current = index;
    activeInstructionRef.current = index;
    setStage(mappedStages[0]);

    mappedStages.slice(1).forEach((nextStage, mappedIndex) => {
      const timer = window.setTimeout(() => {
        setStage(nextStage);
      }, beat * (mappedIndex + 1));
      mobileTimersRef.current.push(timer);
    });

  }, [clearMobileTimers]);

  useEffect(() => {
    const desktopQuery = window.matchMedia("(min-width: 821px)");
    let frame = 0;

    const updateChapter = () => {
      frame = 0;
      if (!desktopQuery.matches) return;

      const readingLine = window.innerHeight * 0.5;
      let closestIndex = activeInstructionRef.current;
      let closestDistance = Number.POSITIVE_INFINITY;

      chapterRefs.current.forEach((chapter, index) => {
        if (!chapter) return;
        const rect = chapter.getBoundingClientRect();
        const chapterCenter = rect.top + rect.height * 0.5;
        const distance = Math.abs(chapterCenter - readingLine);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex === activeInstructionRef.current) return;
      activeInstructionRef.current = closestIndex;
      if (prefersReducedMotion) {
        clearMobileTimers();
        setStage(instructionStages[closestIndex][0]);
      } else {
        playInstruction(closestIndex, 760);
      }
    };

    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(updateChapter);
    };

    requestUpdate();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
    desktopQuery.addEventListener("change", requestUpdate);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      clearMobileTimers();
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
      desktopQuery.removeEventListener("change", requestUpdate);
    };
  }, [clearMobileTimers, playInstruction, prefersReducedMotion]);

  useEffect(() => {
    if (prefersReducedMotion) return;

    const mobileQuery = window.matchMedia("(max-width: 820px)");
    const stepDuration = 3200;

    const clearAutoplay = () => {
      if (mobileAutoplayRef.current !== null) {
        window.clearTimeout(mobileAutoplayRef.current);
        mobileAutoplayRef.current = null;
      }
    };

    const queueNext = () => {
      clearAutoplay();
      if (!mobileQuery.matches || !motionActive) return;

      mobileAutoplayRef.current = window.setTimeout(() => {
        const nextInstruction = (mobileInstructionRef.current + 1) % instructions.length;
        playInstruction(nextInstruction);
        queueNext();
      }, stepDuration);
    };

    const syncAutoplay = () => {
      clearAutoplay();
      clearMobileTimers();
      if (!mobileQuery.matches || !motionActive) return;
      playInstruction(mobileInstructionRef.current);
      queueNext();
    };

    syncAutoplay();
    mobileQuery.addEventListener("change", syncAutoplay);

    return () => {
      clearAutoplay();
      clearMobileTimers();
      mobileQuery.removeEventListener("change", syncAutoplay);
    };
  }, [clearMobileTimers, motionActive, playInstruction, prefersReducedMotion]);

  useEffect(() => {
    const frame = productFrameRef.current;
    if (!frame) return;

    const mobileQuery = window.matchMedia("(max-width: 820px)");
    const desktopCanvasWidth = 760;
    const desktopCanvasHeight = 590;

    const sizeFrame = () => {
      if (!mobileQuery.matches) {
        frame.style.removeProperty("--agent-mobile-scale");
        frame.style.removeProperty("--agent-mobile-height");
        return;
      }

      const availableWidth = frame.clientWidth;
      const scale = Math.min(1, availableWidth / desktopCanvasWidth);
      frame.style.setProperty("--agent-mobile-scale", String(scale));
      frame.style.setProperty(
        "--agent-mobile-height",
        `${Math.ceil(desktopCanvasHeight * scale)}px`
      );
    };

    let resizeFrame = 0;
    const scheduleSizeFrame = () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(sizeFrame);
    };

    window.addEventListener("resize", scheduleSizeFrame, { passive: true });
    mobileQuery.addEventListener("change", scheduleSizeFrame);
    scheduleSizeFrame();

    return () => {
      window.cancelAnimationFrame(resizeFrame);
      window.removeEventListener("resize", scheduleSizeFrame);
      mobileQuery.removeEventListener("change", scheduleSizeFrame);
    };
  }, []);

  const activateInstruction = (index: number) => {
    const mobile = window.matchMedia("(max-width: 820px)").matches;
    activeInstructionRef.current = index;
    if (!prefersReducedMotion) {
      playInstruction(index, mobile ? 900 : 760);
    } else {
      setStage(instructionStages[index][0]);
    }
    if (!mobile) {
      chapterRefs.current[index]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const cursorStyle = {
    transform: `translate3d(${cursor.x}px, ${cursor.y}px, 0)`
  };

  return (
    <div
      className={`agent-demo agent-demo--instruction-${activeInstructionIndex} agent-demo--stage-${stage} ${motionActive ? "is-motion-active" : ""}`}
      ref={motionRef}
    >
      <div className="agent-demo__scrolly">
        <ol className="agent-demo__chapters" aria-label="How Offr works">
          {instructions.map((instruction, index) => (
            <li
              className={`${activeInstructionIndex === index ? "is-active" : ""} ${activeInstructionIndex > index ? "is-complete" : ""}`}
              ref={(element) => { chapterRefs.current[index] = element; }}
              key={instruction.title}
            >
              <button
                type="button"
                aria-current={activeInstructionIndex === index ? "step" : undefined}
                aria-controls="agent-product-preview"
                onClick={() => activateInstruction(index)}
              >
                <span>{String(index + 1).padStart(2, "0")}</span>
                <i aria-hidden="true" />
                <strong>{instruction.title}</strong>
                <p>{instruction.copy}</p>
              </button>
            </li>
          ))}
        </ol>

        <div className="agent-demo__dock">
          <div className="agent-demo__step-panel">
            <ol className="agent-demo__step-list" aria-label="How Offr works">
              {instructions.map((instruction, index) => (
                <li
                  className={`${activeInstructionIndex === index ? "is-active" : ""} ${activeInstructionIndex > index ? "is-complete" : ""}`}
                  key={instruction.title}
                >
                  <button
                    type="button"
                    aria-current={activeInstructionIndex === index ? "step" : undefined}
                    aria-controls="agent-product-preview"
                    onClick={() => activateInstruction(index)}
                  >
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    <strong>
                      <b>{instruction.title}</b>
                      <i>{instruction.copy}</i>
                      <em>{instruction.short}</em>
                    </strong>
                    <Check size={14} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ol>

            <div className="agent-demo__active-readout" aria-live="polite">
              <div className="agent-demo__readout-heading">
                <span className="agent-demo__readout-index" aria-hidden="true">
                  {String(activeInstructionIndex + 1).padStart(2, "0")}
                </span>
                <i aria-hidden="true" />
                <small>
                  Step {activeInstructionIndex + 1} of {instructions.length} · {stages[stage].label}
                </small>
                <strong>{instructions[activeInstructionIndex].title}</strong>
              </div>
              <p>{instructions[activeInstructionIndex].copy}</p>
              <ol className="agent-demo__mobile-progress" aria-label="Choose a workflow step">
                {instructions.map((instruction, index) => (
                  <li key={instruction.title}>
                    <button
                      type="button"
                      aria-current={activeInstructionIndex === index ? "step" : undefined}
                      aria-label={`Step ${index + 1}: ${instruction.title}`}
                      onClick={() => activateInstruction(index)}
                    >
                      <span aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div className="agent-product-wrap">
            <div className="agent-product-frame" ref={productFrameRef}>
              <div
                className={`agent-product agent-product--stage-${stage}`}
                id="agent-product-preview"
                ref={productRef}
                aria-label="Synthetic preview of the five-step Offr workflow"
              >
              <div className="agent-product__chrome">
                <span className="browser-dots" aria-hidden="true"><i /><i /><i /></span>
                <div className="agent-product__tabs" aria-hidden="true">
                  {jobs.map((job, index) => (
                    <span className={index === (submitted ? 1 : 0) ? "is-current" : ""} key={job.name}>
                      <i className={`hero-product__company hero-product__company--${job.tone}`}>{job.mark}</i>
                      <b>{job.role}</b>
                    </span>
                  ))}
                </div>
                <div className="agent-product__tools">
                  <i /><i />
                  <button
                    type="button"
                    data-cursor-target="extension-icon"
                    aria-label="Open the Offr extension"
                    onClick={() => setStage(2)}
                  >
                    <OffrLogo markOnly />
                  </button>
                </div>
              </div>

              <div className="agent-product__body">
                <aside className="hero-product__sidebar agent-product__sidebar" aria-hidden="true">
                  <OffrLogo />
                  <nav>
                    <span><House size={16} /></span>
                    <span className="is-active"><UserRound size={16} /></span>
                    <span><BriefcaseBusiness size={16} /></span>
                    <span><FileText size={16} /></span>
                    <span><Settings size={16} /></span>
                  </nav>
                  <span className="hero-product__safe"><ShieldCheck size={14} /> Review gated</span>
                </aside>

                <div className="agent-product__workspace">
                  <div className={`agent-product__view agent-profile ${activeInstructionIndex === 0 ? "is-visible" : ""}`}>
                    <header className="agent-product__panel-head">
                      <div><small>Profile</small><strong>Your verified source</strong></div>
                      <span><Check size={13} aria-hidden="true" /> Saved once</span>
                    </header>
                    <div className="agent-profile__grid">
                      <label><span>Full name</span><b><i>Aarav Mehta</i></b></label>
                      <label><span>Current city</span><b><i>Bengaluru</i></b></label>
                      <label><span>Core skills</span><b><i>React · TypeScript</i></b></label>
                      <label><span>Experience</span><b><i>3 years</i></b></label>
                    </div>
                    <div className="agent-profile__resume">
                      <FileText size={20} aria-hidden="true" />
                      <span><small>Base resume</small><strong>Aarav-Mehta.pdf</strong></span>
                      <Check size={15} aria-hidden="true" />
                    </div>
                  </div>

                  <div className={`agent-product__view agent-selection ${activeInstructionIndex === 1 ? "is-visible" : ""}`}>
                    <header className="agent-product__panel-head">
                      <div><small>Browser</small><strong>Your chosen applications</strong></div>
                      <span><ListChecks size={13} aria-hidden="true" /> 4 tabs open</span>
                    </header>
                    <div className="agent-selection__summary">
                      <strong>4</strong>
                      <span>roles selected</span>
                      <small>You choose. Offr prepares.</small>
                    </div>
                    <div className="agent-selection__list">
                      {jobs.map((job) => (
                        <div key={job.name}>
                          <i className={`hero-product__company hero-product__company--${job.tone}`}>{job.mark}</i>
                          <span><strong>{job.role}</strong><small>{job.name}</small></span>
                          <Check size={14} aria-hidden="true" />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={`agent-product__view agent-application ${activeInstructionIndex >= 2 ? "is-visible" : ""}`}>
                    <header className="agent-product__panel-head">
                      <div><small>Northstar</small><strong>Frontend Engineer</strong></div>
                      <span>Application 1 of 4</span>
                    </header>
                    <div className={`agent-application__status ${applying ? "is-working" : ""}`}>
                      <span><AgentPulseIcon size={16} /></span>
                      <strong>{agentAction}</strong>
                      <i aria-hidden="true"><b /><b /><b /></i>
                    </div>
                    <div className="agent-application__fields">
                      <label>
                        <span>Full name</span>
                        <b className={stage >= 4 ? "is-filled" : ""}>{stage >= 4 ? "Aarav Mehta" : "Required"}</b>
                      </label>
                      <label>
                        <span>Current city</span>
                        <b className={stage >= 4 ? "is-filled" : ""}>{stage >= 4 ? "Bengaluru" : "Required"}</b>
                      </label>
                      <label className="is-wide">
                        <span>Why this role?</span>
                        <b className={stage >= 5 ? "is-filled" : ""}>
                          {stage >= 5 ? "Product-minded frontend work, in my own voice." : "Short answer"}
                        </b>
                      </label>
                    </div>
                    <div className={`agent-application__resume ${stage >= 6 ? "is-ready" : ""}`}>
                      <FileText size={18} aria-hidden="true" />
                      <span><small>Role resume</small><strong>{stage >= 6 ? "Aarav-Frontend-v3.pdf" : "Queued"}</strong></span>
                      {stage >= 6 ? <Check size={14} aria-hidden="true" /> : <i />}
                    </div>
                  </div>
                </div>
              </div>

              <aside className={`hero-product__extension agent-product__extension ${extensionOpen ? "is-open" : ""} ${applying ? "is-working" : ""}`}>
                <header>
                  <OffrLogo />
                  <span><i /> {applying ? "working" : reviewOpen ? "review" : "ready"}</span>
                </header>
                <div className="hero-product__extension-title dashboard-extension-preview__title">
                  <span>Selected applications</span><strong>4 tabs</strong>
                </div>
                <div className="hero-product__queue dashboard-extension-preview__queue">
                  {jobs.map((job, index) => (
                    <div className={index === (submitted ? 1 : 0) ? "is-current" : ""} key={job.name}>
                      <i className={`hero-product__company hero-product__company--${job.tone} dashboard-extension-preview__mark dashboard-extension-preview__mark--${job.tone}`}>{job.mark}</i>
                      <span><strong>{job.name}</strong><small>{job.role}</small></span>
                      <b>{jobState(index, stage)}</b>
                    </div>
                  ))}
                </div>
                <button
                  className={`hero-product__apply agent-product__apply dashboard-extension-preview__apply ${applying ? "is-applying" : ""} ${reviewOpen ? "is-ready" : ""}`}
                  type="button"
                  data-cursor-target="apply-all"
                  onClick={() => setStage(4)}
                >
                  <span className="agent-product__apply-progress" style={{ width: `${applyProgress}%` }} aria-hidden="true" />
                  <span><AgentPulseIcon size={16} /> {applyLabel}</span>
                  {applying && <i>{applyProgress}%</i>}
                </button>
                <p><CirclePause size={13} aria-hidden="true" /> Sequential · review-gated</p>
              </aside>

              <div className={`agent-product__review ${reviewOpen ? "is-open" : ""} ${submitted ? "is-submitted" : ""}`}>
                <span><FileCheck2 size={19} aria-hidden="true" /></span>
                <div>
                  <small>{submitted ? "Application sent" : "Final checkpoint"}</small>
                  <strong>{submitted ? "Northstar submitted." : "Ready for your review."}</strong>
                </div>
                <button
                  type="button"
                  data-cursor-target="confirm-button"
                  onClick={() => setStage(8)}
                  disabled={submitted}
                >
                  {submitted ? <Check size={15} aria-hidden="true" /> : <>Approve &amp; submit <ChevronRight size={14} aria-hidden="true" /></>}
                </button>
              </div>

              <span
                className={`agent-click ${cursorVisible ? "is-clicking" : ""}`}
                style={cursorStyle}
                aria-hidden="true"
              />
              <MousePointer2
                className={`agent-cursor ${cursor.ready && cursorVisible && !prefersReducedMotion ? "is-ready" : ""}`}
                style={cursorStyle}
                size={29}
                fill="white"
                strokeWidth={2.2}
                aria-hidden="true"
              />
              </div>
            </div>
            <p className="agent-demo__disclaimer">Synthetic preview · planned workflow · not yet live</p>
          </div>
      </div>
    </div>
    </div>
  );
}
